import { useEffect } from "react";
import { useTranslation } from "../hooks/useTranslation";

export const FormatUnit = ({ value, unit }: { value: string | number; unit: string }) => {
  const { siteLang } = useTranslation();
  const isAr = siteLang === "ar";
  const format = new Intl.NumberFormat(isAr ? "ar-EG" : "en-US", {
    style: "unit",
    unit,
  }).format(+value);
  return <>{format}</>;
};

export const FormatPrice = ({ value }: { value: string | number }) => {
  const { siteLang } = useTranslation();
  const isAr = siteLang === "ar";
  const format = new Intl.NumberFormat(isAr ? "ar-EG" : "en-US", {
    maximumFractionDigits: 0,
  }).format(+value);
  return (
    <>
      {format} {isAr ? "جنيه" : "EGP"}
    </>
  );
};

export const FormatNumber = ({ value }: { value: string | number }) => {
  const { siteLang } = useTranslation();
  const isAr = siteLang === "ar";
  const replacedComma = isAr ? /٬/g : /,/g;
  return (
    <>
      {Number(value)
        .toLocaleString(isAr ? "ar-EG" : "en-US")
        .replace(replacedComma, "")}
    </>
  );
};

export const FormatDate = ({ value }: { value: string }) => {
  const { siteLang } = useTranslation();
  const dateObj = new Date(value);
  const format = new Intl.DateTimeFormat(siteLang === "ar" ? "ar-EG" : "en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(dateObj);
  return <>{format}</>;
};

export const useScrollToTop = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
};

/**
 * Formats a phone number by removing leading `+`, `2`, and `0` characters
 * @param phone - The phone number to format (can be number or string)
 * @returns The formatted phone number as a string
 *
 * Here are some example transformations:
 *```
 * +12345678900 → 12345678900
 * +02345678900 → 2345678900
 * 00123456789 → 123456789
 * 201234567890 → 1234567890
 * +201234567890 → 1234567890
 * ```
 */
export function rawPhoneNumber(phone: number | string): string {
  return phone.toString().replace(/^\+/, "").replace(/^2*/, "").replace(/^0*/, "");
}

/**
 * Mapping of Arabic and Persian digits to English digits
 **/
const ARABIC_TO_ENGLISH_DIGIT_MAP: Record<string, string> = {
  // Arabic Indic digits
  "٠": "0",
  "١": "1",
  "٢": "2",
  "٣": "3",
  "٤": "4",
  "٥": "5",
  "٦": "6",
  "٧": "7",
  "٨": "8",
  "٩": "9",
};

/**
 * Convert input to English numbers
 * @param input - Original phone number input
 * @returns Converted phone number with only English digits
 *
 * Here are some example transformations:
 *
 *```
 * ٠١٢٣٤٥٦٧٨٩ → 0123456789
 * ١٢٣٤٥ → 12345
 * Hello ٤٥٦ → Hello 456
 * ```
 *
 */
export const convertToEnglishNumbers = (input: string): string => {
  return input
    .split("")
    .map((char) => ARABIC_TO_ENGLISH_DIGIT_MAP[char] || char)
    .join("");
};
